import type { Message } from '../index';

import { type VoteType } from '@readme/iso';
import { useCallback, useState, useRef } from 'react';

import type { PageContext } from '@Hub/context/CurrentPageContext';

import fetcher from '@core/hooks/useReadmeApi/fetcher';

interface UseChatInterfaceProps {
  childSubdomain?: string;
  messages: Message[];
  onError: (error: string) => void;
  onMessage: (message: Message) => void;
  onStreamUpdate?: (partialContent: string) => void;
  pageContext: PageContext | undefined;
  projectId: string;
}

export function useChatInterface({
  childSubdomain,
  messages,
  onMessage,
  onError,
  onStreamUpdate,
  pageContext,
  projectId,
}: UseChatInterfaceProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  /**
   * Abort the current streaming request
   */
  const abortStream = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setIsLoading(false);
      setStreamingMessageId(null);
    }
  }, []);

  /**
   * Process streaming response from the server
   * Handles chunked data and updates message content in real-time
   */
  const processStreamReader = useCallback(
    async (reader: ReadableStreamDefaultReader<Uint8Array>, decoder: TextDecoder, messageId: string) => {
      let accumulatedContent = '';

      const processChunk = async (): Promise<void> => {
        try {
          const { value, done } = await reader.read();

          if (done) {
            if (accumulatedContent) {
              const finalMessage: Message = {
                id: messageId,
                role: 'ai',
                content: accumulatedContent,
                timestamp: new Date(),
              };
              onMessage(finalMessage);
            }
            setStreamingMessageId(null);
            abortControllerRef.current = null;
            return;
          }

          const chunkText = decoder.decode(value, { stream: true });

          if (chunkText.startsWith('error: ')) {
            const errorMessage = chunkText.replace('error: ', '').trim();
            onError(errorMessage);
          } else {
            accumulatedContent += chunkText;
            if (onStreamUpdate) {
              onStreamUpdate(accumulatedContent);
            }
          }

          await processChunk();
        } catch (error) {
          if (error instanceof Error && error.name === 'AbortError') {
            setStreamingMessageId(null);
            abortControllerRef.current = null;
            return;
          }
          throw error;
        }
      };

      return processChunk();
    },
    [onError, onMessage, onStreamUpdate],
  );

  /**
   * Send a message to the chat API with streaming response
   */
  const sendMessage = useCallback(
    async (content: string) => {
      if (!content.trim() || isLoading) {
        return;
      }

      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      setIsLoading(true);
      const messageId = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      setStreamingMessageId(messageId);

      try {
        const apiUrl = `${childSubdomain ? `/${childSubdomain}` : ''}/chatgpt/ask`;

        const conversationMessages = messages.map(msg => ({
          role: msg.role,
          content: msg.content,
        }));

        conversationMessages.push({
          role: 'user',
          content,
        });

        const payload: { messages: typeof conversationMessages; pageContext?: PageContext } = {
          messages: conversationMessages,
        };

        if (pageContext) {
          payload.pageContext = pageContext;
        }

        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
          signal: abortController.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        if (!response.body) {
          throw new Error('Response body is empty');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        await processStreamReader(reader, decoder, messageId);
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          setStreamingMessageId(null);
          abortControllerRef.current = null;
          return;
        }

        const errorMessage = error instanceof Error ? error.message : 'Failed to communicate with AI service';
        onError(errorMessage);
        setStreamingMessageId(null);
        abortControllerRef.current = null;
      } finally {
        setIsLoading(false);
      }
    },
    [childSubdomain, messages, isLoading, onError, processStreamReader, pageContext],
  );

  /**
   * Send a vote for a message
   */
  const sendVote = useCallback(
    async (messageContent: string, question: string, voteType: VoteType) => {
      try {
        const apiUrl = `${childSubdomain ? `/${childSubdomain}` : ''}/chatgpt/vote`;

        const result = await fetcher<number | string>(apiUrl, {
          method: 'POST',
          body: JSON.stringify({
            answer: messageContent,
            prompt: question,
            projectID: projectId,
            direction: voteType,
          }),
        });

        if (result === 200 || result === '200') return true;
        if (result === 400 || result === '400') return false;

        return true;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to send vote';
        onError(errorMessage);
        return false;
      }
    },
    [childSubdomain, onError, projectId],
  );

  return {
    sendMessage,
    sendVote,
    abortStream,
    isLoading,
    streamingMessageId,
  };
}
